/**
 * <s-product-gallery> — product media viewer with a vertical thumbnail rail.
 *
 * - [data-gallery-track]: horizontal scroll-snap strip of slides (one visible).
 * - [data-gallery-thumb][data-index]: rail buttons that bring a slide into view.
 * - [data-rail-prev]/[data-rail-next]: top/bottom rail arrows (only when thumbnails
 *   overflow the main image height). Each click selects the previous/next image and
 *   shows it in the main area. On load the rail is top-aligned with the first image
 *   selected and only the bottom arrow shows; the top arrow appears once past the first
 *   image, and each arrow hides at its end. The selected thumbnail is centred in the
 *   rail, clamped so the first/last few stay flush with the top/bottom edge.
 * - Arrow keys (←/→ and ↑/↓) step images while focus is inside the gallery.
 * - [data-gallery-counter]: "n / total" text, updated as slides change (mobile).
 * - [data-variant-media]: JSON map of variant id -> slide index; when the
 *   product form's hidden variant input changes, the gallery shows that slide.
 */
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.rail = this.querySelector('[data-gallery-rail]');
    this.prevButtons = [...this.querySelectorAll('[data-rail-prev]')];
    this.nextButtons = [...this.querySelectorAll('[data-rail-next]')];
    this.counter = this.querySelector('[data-gallery-counter]');
    this.activeIndex = 0;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    try {
      this.variantMedia = JSON.parse(this.querySelector('[data-variant-media]')?.textContent || '{}');
    } catch {
      this.variantMedia = {};
    }

    this.onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const arrow = event.target.closest('[data-rail-prev], [data-rail-next]');
      if (arrow) {
        this.step(arrow.hasAttribute('data-rail-prev') ? -1 : 1);
        // An arrow hides at its end; hand keyboard focus to the selected thumbnail instead of losing it.
        if (arrow.hidden && document.activeElement === arrow) this.thumbs[this.activeIndex]?.focus({ preventScroll: true });
        return;
      }
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) this.show(Number(thumb.dataset.index));
    };
    this.onKeydown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target instanceof Element && event.target.closest('input, select, textarea, dialog')) return;
      const isRtl = getComputedStyle(this).direction === 'rtl';
      const back = isRtl ? 'ArrowRight' : 'ArrowLeft';
      const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
      if (event.key === back || event.key === 'ArrowUp') {
        event.preventDefault();
        this.step(-1, true);
      } else if (event.key === forward || event.key === 'ArrowDown') {
        event.preventDefault();
        this.step(1, true);
      }
    };
    this.onTrackScroll = () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.syncFromScroll());
    };

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    this.onRailScrollEnd = () => { this.railTarget = null; };
    this.rail?.addEventListener('scrollend', this.onRailScrollEnd);
    this.onTrackScrollEnd = () => this.clearPending();
    this.track.addEventListener('scrollend', this.onTrackScrollEnd);

    this.resizeObserver = new ResizeObserver(() => {
      this.updateArrows();
      this.keepThumbVisible(this.thumbs[this.activeIndex], 'auto');
    });
    if (this.rail) this.resizeObserver.observe(this.rail);

    this.observeVariant();
    this.setActive(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.track?.removeEventListener('scroll', this.onTrackScroll);
    this.track?.removeEventListener('scrollend', this.onTrackScrollEnd);
    this.rail?.removeEventListener('scrollend', this.onRailScrollEnd);
    clearTimeout(this.pendingTimer);
    clearTimeout(this.railTimer);
    this.resizeObserver?.disconnect();
    this.variantObserver?.disconnect();
  }

  get behavior() {
    return this.reduceMotion.matches ? 'auto' : 'smooth';
  }

  step(direction, fromKeyboard = false) {
    const index = this.activeIndex + direction;
    if (index < 0 || index >= this.slides.length) return;
    this.show(index);
    // Keep keyboard focus on a meaningful control: the matching thumbnail when focus was in the rail.
    if (fromKeyboard && this.rail?.contains(document.activeElement)) this.thumbs[index]?.focus({ preventScroll: true });
  }

  show(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const left = isRtl ? -(this.track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    // While the main strip glides to the target it passes the slides in between; ignore those
    // intermediate positions so the rail makes one smooth move instead of re-centring per slide.
    this.pendingIndex = index;
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => this.clearPending(), 900);
    this.track.scrollTo({ left, behavior: this.behavior });
    this.setActive(index);
    if (Math.abs(this.track.scrollLeft - left) < 1) this.clearPending();
  }

  clearPending() {
    this.pendingIndex = null;
    clearTimeout(this.pendingTimer);
  }

  syncFromScroll() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    if (this.pendingIndex != null) {
      if (index === this.pendingIndex) this.clearPending();
      return;
    }
    if (index !== this.activeIndex) this.setActive(index);
  }

  setActive(index) {
    this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.thumbs.forEach((thumb, i) => {
      if (i === this.activeIndex) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.counter) this.counter.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
    this.keepThumbVisible(this.thumbs[this.activeIndex]);
    this.updateArrows();
  }

  keepThumbVisible(thumb, behavior = this.behavior) {
    if (!thumb || !this.rail || this.rail.clientHeight === 0) return;
    const maxTop = this.rail.scrollHeight - this.rail.clientHeight;
    if (maxTop <= 1) return;
    // Centre the active thumbnail so its neighbours on both sides stay in view; clamped so the
    // first/last few stay flush with the rail edges.
    const item = thumb.closest('li') || thumb;
    const top = Math.round(Math.max(0, Math.min(item.offsetTop - (this.rail.clientHeight - item.offsetHeight) / 2, maxTop)));
    // Skip no-op moves (e.g. stepping among the first few, already at the top) and repeats of an
    // in-flight target, so a running smooth scroll is never restarted.
    if (Math.abs(this.rail.scrollTop - top) < 1 || (behavior === 'smooth' && this.railTarget === top)) return;
    this.railTarget = top;
    clearTimeout(this.railTimer);
    this.railTimer = setTimeout(() => { this.railTarget = null; }, 900);
    this.rail.scrollTo({ top, behavior });
  }

  updateArrows() {
    const atStart = this.activeIndex <= 0;
    const atEnd = this.activeIndex >= this.slides.length - 1;
    const railOverflows = !!this.rail && this.rail.scrollHeight - this.rail.clientHeight > 1;
    for (const button of this.prevButtons) {
      const isRail = button.hasAttribute('data-rail-prev');
      button.hidden = atStart || (isRail && !railOverflows);
    }
    for (const button of this.nextButtons) {
      const isRail = button.hasAttribute('data-rail-next');
      button.hidden = atEnd || (isRail && !railOverflows);
    }
  }

  observeVariant() {
    const form = document.querySelector('[data-product-form]');
    if (!form || Object.keys(this.variantMedia).length === 0) return;
    this.lastVariantId = form.querySelector('input[name="id"]')?.value;
    this.variantObserver = new MutationObserver(() => {
      const variantId = form.querySelector('input[name="id"]')?.value;
      if (!variantId || variantId === this.lastVariantId) return;
      this.lastVariantId = variantId;
      const index = this.variantMedia[variantId];
      if (typeof index === 'number') this.show(index);
    });
    this.variantObserver.observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value'] });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
